import React from "react";
import { PhoneInput } from "../PhoneInput";
import { useApplication } from "../ApplicationProvider";
import { usePublicConfig } from "../PublicConfigProvider";
import { YesNoRadio } from "../YesNoRadio";
import { YesNoWithDetail } from "../YesNoWithDetail";
import { TemplateUpload } from "../TemplateUpload";
import { FileUpload } from "../FileUpload";
import { SignaturePad } from "../SignaturePad";
import { formatIsoDate } from "../../config";
import { HEALTH_CONDITIONS } from "../../healthConditions";
import { ALLERGY_CATEGORIES, SELF_CARRY_MED_TYPES } from "../../types";
import type {
  AllergyCategory,
  DiseaseKey,
  DoseForm,
  OtcPermission,
  SelfCarryMedType,
  TimeOfDay,
} from "../../types";
import {
  OTC_ITEMS,
  OTC_QUESTION_FIELDS,
  OTC_BLANK_DEFAULT_NOTE,
  otcIntroText,
  otcAcknowledgementText,
  otcPermissionFor,
  setOtcSelection,
} from "../../otcMedications";
import {
  TOPICAL_ITEMS,
  TOPICALS_BLANK_DEFAULT_NOTE,
  topicalsAcknowledgementText,
  topicalsIntroText,
} from "../../topicalsAuthorization";
import {
  otcAcknowledgementComplete,
  otcFollowupComplete,
  topicalsAcknowledgementComplete,
  OTC_ACK_REQUIRED_MESSAGE,
  TOPICALS_ACK_REQUIRED_MESSAGE,
  phonePlaceholderFor,
} from "../../validation";

const DISEASE_ROWS: Array<{ key: DiseaseKey; label: string }> = [
  { key: "chickenPox", label: "Chicken Pox" },
  { key: "germanMeasles", label: "German Measles" },
  { key: "measles", label: "Measles" },
  { key: "mumps", label: "Mumps" },
];

const DOSE_FORMS: DoseForm[] = ["tablet", "capsule", "liquid", "inhaler", "injection", "topical", "drops", "other"];

const TIMES_OF_DAY: Array<{ key: TimeOfDay; label: string }> = [
  { key: "morning", label: "Morning" },
  { key: "noon", label: "Noon" },
  { key: "evening", label: "Evening" },
  { key: "prn", label: "PRN (As Needed)" },
  { key: "other", label: "Other" },
];

// Displayed for the parent's reference; the single "vaccines up to date" answer
// below drives validation (per requirements), not per-vaccine checkboxes.
const IMMUNIZATION_VACCINES = [
  "Chickenpox (Varicella)",
  "Diphtheria, Tetanus, and Pertussis (DTaP)",
  "Hepatitis B",
  "Measles, Mumps, and Rubella (MMR)",
  "Pertussis",
  "Polio (IPV)",
  "Tetanus",
];

const OTC_PERMISSION_OPTIONS: Array<{ value: OtcPermission; label: string }> = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "call", label: "Call Me First" },
];

/**
 * The three-way Yes / No / Call Me First control used by the OTC selections
 * table. Round 20 factors the existing inline markup out verbatim (same classes,
 * same radio-group naming scheme, same "blank shows nothing selected" behaviour)
 * so the new Sunscreen & Insect Repellent rows reuse the very same control
 * rather than a second, subtly different one. `value` is null while unanswered.
 */
function PermissionRadioGroup({
  name,
  value,
  onChange,
}: {
  name: string;
  value: OtcPermission | null;
  onChange: (value: OtcPermission) => void;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
      {OTC_PERMISSION_OPTIONS.map((opt) => (
        <label key={opt.value} className="flex items-center gap-1 text-xs whitespace-nowrap">
          <input
            type="radio"
            name={name}
            className="h-4 w-4 border-slate-300 text-program focus:ring-program"
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

const OTC_MEDICATION_NOTE =
  "List any prescription or regular medications the student will need during the session, " +
  "including strength, dose, and time(s) of day. A Physician Medication Authorization form is " +
  "required for any medication listed here. Common over-the-counter medications (pain relievers, " +
  "allergy medicine, and similar) are covered separately below under Over-the-Counter Medication " +
  "Authorization.";

export function Step3Health({ errors }: { errors: Record<string, string> }) {
  const { data, update, addMedicationRow, removeMedicationRow, addAllergyRow, removeAllergyRow } = useApplication();
  const cfg = usePublicConfig();

  const takesMeds = data.step3.takesMedications === "Yes";
  const hasAnyMedication = takesMeds && data.step3.medications.some((m) => m.name.trim().length > 0);
  const hasAllergiesYes = data.step3.hasAllergies === "Yes";
  const programWord = cfg.program === "boys" ? "Boys" : "Girls";

  const selfCarries = data.step3.selfCarriesEmergencyMeds === "Yes";

  // Round 24 — phone format follows the application's address type, so the
  // hint on the PCP and emergency-contact fields has to follow it too.
  const phonePlaceholder = phonePlaceholderFor(data.step2.addressType);

  // Read source for the OTC per-item controls (safe for pre-Round-18 drafts).
  const otcSelectionsSource = data.step3.otcAuthorization;

  const setOtcPermission = (id: string, permission: OtcPermission) =>
    update((d) => {
      d.step3.otcAuthorization.selections = setOtcSelection(
        d.step3.otcAuthorization.selections ?? [],
        id,
        permission
      );
    });

  const toggleSelfCarryType = (key: SelfCarryMedType, checked: boolean) =>
    update((d) => {
      const current = new Set(d.step3.selfCarryMedTypes ?? []);
      if (checked) current.add(key);
      else current.delete(key);
      d.step3.selfCarryMedTypes = SELF_CARRY_MED_TYPES.filter((t) => current.has(t));
      if (key === "Other" && !checked) d.step3.selfCarryMedOther = "";
    });

  const toggleMedTime = (index: number, key: TimeOfDay, checked: boolean) =>
    update((d) => {
      const current = new Set(d.step3.medications[index].timesOfDay ?? []);
      if (checked) current.add(key);
      else current.delete(key);
      d.step3.medications[index].timesOfDay = TIMES_OF_DAY.map((t) => t.key).filter((k) =>
        current.has(k)
      );
      if (key === "other" && !checked) d.step3.medications[index].timeOfDayOther = "";
    });

  return (
    <section className="space-y-6">
      <div className="section-card">
        <h2 className="text-xl font-semibold mb-1">Primary Care Physician</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Name</label>
            <input
              className="field-input"
              value={data.step3.pcpName}
              onChange={(e) =>
                update((d) => {
                  d.step3.pcpName = e.target.value;
                })
              }
            />
            {errors["pcpName"] && <p className="field-error">{errors["pcpName"]}</p>}
          </div>
          <div>
            <label className="field-label">Phone</label>
            <PhoneInput
              placeholder={phonePlaceholder}
              addressType={data.step2.addressType}
              value={data.step3.pcpPhone}
              onChange={(v) =>
                update((d) => {
                  d.step3.pcpPhone = v;
                })
              }
            />
            {errors["pcpPhone"] && <p className="field-error">{errors["pcpPhone"]}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Address</label>
            <input
              className="field-input"
              value={data.step3.pcpAddress}
              onChange={(e) =>
                update((d) => {
                  d.step3.pcpAddress = e.target.value;
                })
              }
            />
            {errors["pcpAddress"] && <p className="field-error">{errors["pcpAddress"]}</p>}
          </div>
        </div>
      </div>

      {/* Round 16 — Emergency Contact moved here from Step 2. Still required (per
          Round 14), but free-nav: it counts toward Step 3 completeness and is NOT
          part of the Step 1 start gate. The fields still live on data.step2. */}
      <div className="section-card">
        <h2 className="text-xl font-semibold mb-1">Emergency Contact</h2>
        <p className="field-hint mb-3">Enter a trusted person for us to contact in case parent/guardian is unavailable in an emergency.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="field-label">Name</label>
            <input
              className="field-input"
              value={data.step2.emergencyContactName}
              onChange={(e) =>
                update((d) => {
                  d.step2.emergencyContactName = e.target.value;
                })
              }
            />
            {errors["emergencyContactName"] && <p className="field-error">{errors["emergencyContactName"]}</p>}
          </div>
          <div>
            <label className="field-label">Relationship</label>
            <input
              className="field-input"
              value={data.step2.emergencyContactRelationship}
              onChange={(e) =>
                update((d) => {
                  d.step2.emergencyContactRelationship = e.target.value;
                })
              }
            />
            {errors["emergencyContactRelationship"] && <p className="field-error">{errors["emergencyContactRelationship"]}</p>}
          </div>
          <div>
            <label className="field-label">Phone</label>
            <PhoneInput
              placeholder={phonePlaceholder}
              addressType={data.step2.addressType}
              value={data.step2.emergencyContactPhone}
              onChange={(v) =>
                update((d) => {
                  d.step2.emergencyContactPhone = v;
                })
              }
            />
            {errors["emergencyContactPhone"] && <p className="field-error">{errors["emergencyContactPhone"]}</p>}
          </div>
        </div>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Health History</h2>
        <p className="text-sm text-slate-600 mb-4">
          Do you currently have or have you ever been treated for any of the following?
        </p>
        <div className="divide-y divide-slate-200 border border-slate-200 rounded-md">
          {HEALTH_CONDITIONS.map((spec) => {
            const entry = data.step3.healthConditions[spec.id];
            const isYes = entry.answer === "Yes";
            return (
              <div key={spec.id} className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="flex-1 text-sm">{spec.label}</span>
                  <YesNoRadio
                    name={`healthCondition_${spec.id}`}
                    value={entry.answer}
                    onChange={(v) =>
                      update((d) => {
                        const e = d.step3.healthConditions[spec.id];
                        e.answer = v;
                        // Clear the conditional follow-ups when switching to No
                        // so stale details can't leak into the PDF/tracker.
                        if (v === "No") {
                          e.explanation = "";
                          e.extraText = "";
                          e.extraYesNo = null;
                          e.extraDate = "";
                        }
                      })
                    }
                    error={errors[`healthConditions.${spec.id}.answer`]}
                  />
                </div>
                {isYes && (
                  <div className="mt-2 mb-1 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div>
                      <label className="field-label">{spec.explanationLabel}</label>
                      <textarea
                        className="field-input"
                        rows={2}
                        value={entry.explanation}
                        onChange={(e) =>
                          update((d) => {
                            d.step3.healthConditions[spec.id].explanation = e.target.value;
                          })
                        }
                      />
                      {errors[`healthConditions.${spec.id}.explanation`] && (
                        <p className="field-error">{errors[`healthConditions.${spec.id}.explanation`]}</p>
                      )}
                    </div>
                    {spec.extraText && (
                      <div>
                        <label className="field-label">{spec.extraText.label}</label>
                        <input
                          className="field-input"
                          value={entry.extraText}
                          onChange={(e) =>
                            update((d) => {
                              d.step3.healthConditions[spec.id].extraText = e.target.value;
                            })
                          }
                        />
                      </div>
                    )}
                    {spec.extraYesNo && (
                      <YesNoRadio
                        name={`healthCondition_${spec.id}_extraYesNo`}
                        label={spec.extraYesNo.label}
                        value={entry.extraYesNo}
                        onChange={(v) =>
                          update((d) => {
                            d.step3.healthConditions[spec.id].extraYesNo = v;
                          })
                        }
                        error={errors[`healthConditions.${spec.id}.extraYesNo`]}
                      />
                    )}
                    {spec.extraDate && (
                      <div className="max-w-xs">
                        <label className="field-label">{spec.extraDate.label} (optional)</label>
                        <input
                          type="date"
                          className="field-input"
                          value={entry.extraDate}
                          onChange={(e) =>
                            update((d) => {
                              d.step3.healthConditions[spec.id].extraDate = e.target.value;
                            })
                          }
                        />
                      </div>
                    )}
                    {/* The asthma row is the single asthma question — Yes here
                        triggers the Asthma Action Plan template download and the
                        conditional required upload, exactly as before. */}
                    {spec.id === "asthma" && (
                      <TemplateUpload
                        templateName="asthma-action-plan"
                        fieldKey="asthma-action-plan"
                        helper="Download this form, have it completed by your physician, then upload it here."
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Diseases</h2>
        <p className="text-sm text-slate-600 mb-4">Has the applicant had any of the following?</p>
        <div className="divide-y divide-slate-200 border border-slate-200 rounded-md">
          {DISEASE_ROWS.map((row) => (
            <div key={row.key} className="flex flex-wrap items-center gap-4 px-3 py-2">
              <span className="flex-1 text-sm">{row.label}</span>
              <YesNoRadio
                name={`disease_${row.key}`}
                value={data.step3.diseases[row.key]}
                onChange={(v) =>
                  update((d) => {
                    d.step3.diseases[row.key] = v;
                  })
                }
                error={errors[`diseases.${row.key}`]}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Medications</h2>
        <YesNoRadio
          name="takesMedications"
          label="Will the student take any medications during the session?"
          value={data.step3.takesMedications}
          onChange={(v) =>
            update((d) => {
              d.step3.takesMedications = v;
            })
          }
          error={errors["takesMedications"]}
        />
        <p className="text-sm text-slate-600 mt-3">{OTC_MEDICATION_NOTE}</p>

        {takesMeds && (
          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-4">
              List every medication the applicant will bring to the session.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                    <th className="pr-2 pb-1">Medication</th>
                    <th className="pr-2 pb-1">Strength</th>
                    <th className="pr-2 pb-1">Dose qty</th>
                    <th className="pr-2 pb-1">Dose form</th>
                    <th className="pr-2 pb-1">Time(s) of day</th>
                    <th className="pb-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.step3.medications.map((med, i) => (
                    <tr key={i}>
                      <td className="pr-2 py-1 align-top">
                        <input
                          className="field-input"
                          value={med.name}
                          onChange={(e) =>
                            update((d) => {
                              d.step3.medications[i].name = e.target.value;
                            })
                          }
                        />
                      </td>
                      <td className="pr-2 py-1 align-top">
                        <input
                          className="field-input"
                          value={med.strength}
                          onChange={(e) =>
                            update((d) => {
                              d.step3.medications[i].strength = e.target.value;
                            })
                          }
                        />
                        {errors[`medications.${i}.strength`] && (
                          <p className="field-error">{errors[`medications.${i}.strength`]}</p>
                        )}
                      </td>
                      <td className="pr-2 py-1 align-top">
                        <input
                          className="field-input"
                          value={med.doseQty}
                          onChange={(e) =>
                            update((d) => {
                              d.step3.medications[i].doseQty = e.target.value;
                            })
                          }
                        />
                        {errors[`medications.${i}.doseQty`] && (
                          <p className="field-error">{errors[`medications.${i}.doseQty`]}</p>
                        )}
                      </td>
                      <td className="pr-2 py-1 align-top">
                        <select
                          className="field-input"
                          value={med.doseForm}
                          onChange={(e) =>
                            update((d) => {
                              d.step3.medications[i].doseForm = e.target.value as DoseForm | "";
                            })
                          }
                        >
                          <option value="">Select…</option>
                          {DOSE_FORMS.map((df) => (
                            <option key={df} value={df}>
                              {df}
                            </option>
                          ))}
                        </select>
                        {errors[`medications.${i}.doseForm`] && (
                          <p className="field-error">{errors[`medications.${i}.doseForm`]}</p>
                        )}
                      </td>
                      <td className="pr-2 py-1 align-top">
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {TIMES_OF_DAY.map((t) => (
                            <label
                              key={t.key}
                              className="flex items-center gap-1 text-xs whitespace-nowrap"
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-program focus:ring-program"
                                checked={(med.timesOfDay ?? []).includes(t.key)}
                                onChange={(e) => toggleMedTime(i, t.key, e.target.checked)}
                              />
                              {t.label}
                            </label>
                          ))}
                        </div>
                        {(med.timesOfDay ?? []).includes("other") && (
                          <input
                            className="field-input mt-1"
                            placeholder="Describe other timing (optional)"
                            value={med.timeOfDayOther}
                            onChange={(e) =>
                              update((d) => {
                                d.step3.medications[i].timeOfDayOther = e.target.value;
                              })
                            }
                          />
                        )}
                        {errors[`medications.${i}.timesOfDay`] && (
                          <p className="field-error">{errors[`medications.${i}.timesOfDay`]}</p>
                        )}
                      </td>
                      <td className="py-1 align-top">
                        <button
                          type="button"
                          onClick={() => removeMedicationRow(i)}
                          disabled={data.step3.medications.length <= 1}
                          className="text-xs text-red-600 underline disabled:opacity-30"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={addMedicationRow} className="btn-secondary text-xs mt-2">
              + Add medication
            </button>

            {hasAnyMedication && (
              <div className="mt-4">
                <TemplateUpload
                  templateName="medication-authorization"
                  fieldKey="medication-authorization"
                  helper="Because you entered one or more medications, this form must be completed by your physician and uploaded here."
                />
              </div>
            )}

            {hasAnyMedication && (
              <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-900 mb-3">
                  All medications for the student have been entered above. Any student arriving to Maryland {programWord} State
                  without medications, or without the medications being documented in the application portal, shall be refused
                  enrollment at the registration desk. Parents shall be required to provide transportation for any such student
                  back to their home of record.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SignaturePad
                    label="Parent / Guardian medication acknowledgement signature"
                    value={data.step3.medicationAckSignature}
                    onChange={(v) =>
                      update((d) => {
                        d.step3.medicationAckSignature = v;
                      })
                    }
                    error={errors["medicationAckSignature"]}
                  />
                  <div>
                    <label className="field-label">Date</label>
                    <input
                      type="date"
                      className="field-input"
                      value={data.step3.medicationAckSignatureDate}
                      onChange={(e) =>
                        update((d) => {
                          d.step3.medicationAckSignatureDate = e.target.value;
                        })
                      }
                    />
                    {errors["medicationAckSignatureDate"] && (
                      <p className="field-error">{errors["medicationAckSignatureDate"]}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 border-t border-slate-200 pt-4">
          <YesNoRadio
            name="selfCarriesEmergencyMeds"
            label="Will the student carry emergency medication on their person (e.g., inhaler, EpiPen)?"
            value={data.step3.selfCarriesEmergencyMeds}
            onChange={(v) =>
              update((d) => {
                d.step3.selfCarriesEmergencyMeds = v;
                // Hide-and-clear on No so stale selections can't leak downstream.
                if (v === "No") {
                  d.step3.selfCarryMedTypes = [];
                  d.step3.selfCarryMedOther = "";
                }
              })
            }
            error={errors["selfCarriesEmergencyMeds"]}
          />
          <p className="mt-1 text-xs text-slate-500">
            A self-carried emergency medication must also be listed in the medications table above,
            and the physician's medication authorization must cover self-carry.
          </p>
          {errors["selfCarryMedicationRequired"] && (
            <p className="field-error">{errors["selfCarryMedicationRequired"]}</p>
          )}
          {selfCarries && (
            <div className="mt-3">
              <span className="field-label">Which emergency medication(s)?</span>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {SELF_CARRY_MED_TYPES.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-program focus:ring-program"
                      checked={data.step3.selfCarryMedTypes.includes(t)}
                      onChange={(e) => toggleSelfCarryType(t, e.target.checked)}
                    />
                    {t}
                  </label>
                ))}
              </div>
              {errors["selfCarryMedTypes"] && (
                <p className="field-error">{errors["selfCarryMedTypes"]}</p>
              )}
              {data.step3.selfCarryMedTypes.includes("Other") && (
                <div className="mt-2 max-w-md">
                  <label className="field-label">Describe the other emergency medication</label>
                  <input
                    className="field-input"
                    value={data.step3.selfCarryMedOther}
                    onChange={(e) =>
                      update((d) => {
                        d.step3.selfCarryMedOther = e.target.value;
                      })
                    }
                  />
                  {errors["selfCarryMedOther"] && (
                    <p className="field-error">{errors["selfCarryMedOther"]}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Allergies</h2>
        <YesNoRadio
          name="hasAllergies"
          label="Does the student have any known allergies?"
          value={data.step3.hasAllergies}
          onChange={(v) =>
            update((d) => {
              d.step3.hasAllergies = v;
            })
          }
          error={errors["hasAllergies"]}
        />

        {hasAllergiesYes && (
          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-4">List all known allergies and reactions.</p>
            <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                <th className="pr-2 pb-1">Allergy</th>
                <th className="pr-2 pb-1">Category</th>
                <th className="pr-2 pb-1">Reaction</th>
                <th className="pr-2 pb-1">Anaphylaxis risk?</th>
                <th className="pr-2 pb-1">Bringing Epi-Pen?</th>
                <th className="pb-1"></th>
              </tr>
            </thead>
            <tbody>
              {data.step3.allergies.map((al, i) => (
                <tr key={i}>
                  <td className="pr-2 py-1">
                    <input
                      className="field-input"
                      value={al.allergy}
                      onChange={(e) =>
                        update((d) => {
                          d.step3.allergies[i].allergy = e.target.value;
                        })
                      }
                    />
                  </td>
                  <td className="pr-2 py-1">
                    <select
                      className="field-input"
                      value={al.category}
                      onChange={(e) =>
                        update((d) => {
                          d.step3.allergies[i].category = e.target.value as AllergyCategory | "";
                        })
                      }
                    >
                      <option value="">Select…</option>
                      {ALLERGY_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    {errors[`allergies.${i}.category`] && (
                      <p className="field-error">{errors[`allergies.${i}.category`]}</p>
                    )}
                  </td>
                  <td className="pr-2 py-1">
                    <input
                      className="field-input"
                      value={al.reaction}
                      onChange={(e) =>
                        update((d) => {
                          d.step3.allergies[i].reaction = e.target.value;
                        })
                      }
                    />
                    {errors[`allergies.${i}.reaction`] && (
                      <p className="field-error">{errors[`allergies.${i}.reaction`]}</p>
                    )}
                  </td>
                  <td className="pr-2 py-1">
                    <YesNoRadio
                      name={`anaphylaxis_${i}`}
                      value={al.anaphylaxisRisk}
                      onChange={(v) =>
                        update((d) => {
                          d.step3.allergies[i].anaphylaxisRisk = v;
                        })
                      }
                      error={errors[`allergies.${i}.anaphylaxisRisk`]}
                    />
                  </td>
                  <td className="pr-2 py-1">
                    <YesNoRadio
                      name={`epipen_${i}`}
                      value={al.bringingEpiPen}
                      onChange={(v) =>
                        update((d) => {
                          d.step3.allergies[i].bringingEpiPen = v;
                        })
                      }
                      error={errors[`allergies.${i}.bringingEpiPen`]}
                    />
                  </td>
                  <td className="py-1">
                    <button
                      type="button"
                      onClick={() => removeAllergyRow(i)}
                      disabled={data.step3.allergies.length <= 1}
                      className="text-xs text-red-600 underline disabled:opacity-30"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
            <button type="button" onClick={addAllergyRow} className="btn-secondary text-xs mt-2">
              + Add allergy
            </button>
          </div>
        )}
      </div>

      {/* Round 20 — Sunscreen & Insect Repellent Authorization. Deliberately a
          STANDALONE section placed immediately BEFORE the OTC block, not merged
          into it: a parent can authorize one product without the other, and the
          two consent types must never blur together. Both products are named by
          brand (required by the state). The four give/assist permissions are
          OPTIONAL — a blank reads as "No / not authorized" downstream, exactly
          like a blank OTC per-item selection — so only the acknowledgement +
          signature gate Step 3. These are NOT medications: no dose/frequency,
          no administration logging, and no Physician Medication Authorization. */}
      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Sunscreen &amp; Insect Repellent Authorization</h2>
        <p className="text-sm text-slate-700 mb-2">{topicalsIntroText(cfg.programName)}</p>
        <p className="text-xs text-slate-500 mb-4 italic">{TOPICALS_BLANK_DEFAULT_NOTE}</p>

        <div className="space-y-5">
          {TOPICAL_ITEMS.map((item) => (
            <div key={item.key} className="rounded-md border border-slate-200 p-4">
              <h3 className="font-medium text-slate-800">
                {item.shortLabel} <span className="text-slate-600">({item.brand})</span>
              </h3>
              <div className="mt-3 space-y-3">
                {item.permissions.map((p) => (
                  <div
                    key={p.key}
                    className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <span className="text-sm text-slate-700 sm:pr-4">{p.label}</span>
                    <div className="sm:shrink-0">
                      <PermissionRadioGroup
                        name={`topical_${item.key}_${p.key}`}
                        value={data.step3.topicalsAuthorization?.[item.key]?.[p.key] ?? null}
                        onChange={(v) =>
                          update((d) => {
                            d.step3.topicalsAuthorization[item.key][p.key] = v;
                          })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-700 mb-3">
            {topicalsAcknowledgementText(cfg.programName)}
          </p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-program focus:ring-program"
              checked={!!data.step3.topicalsAuthorization?.acknowledged}
              onChange={(e) =>
                update((d) => {
                  d.step3.topicalsAuthorization.acknowledged = e.target.checked;
                })
              }
            />
            <span>I have read and acknowledge the above.</span>
          </label>
          <div className="mt-4 max-w-md">
            <SignaturePad
              label="Parent / Guardian signature"
              value={data.step3.topicalsAuthorization?.acknowledgementSignature ?? ""}
              onChange={(v) =>
                update((d) => {
                  d.step3.topicalsAuthorization.acknowledgementSignature = v || null;
                })
              }
              error={errors["topicalsAuthorization.acknowledgementSignature"]}
            />
          </div>
          {/* Standalone live check: surfaces on a partly-filled Step 3 even though
              the object-level superRefine is skipped by Zod until every other
              required Step 3 field is filled (Round 14 gotcha). */}
          {(errors["topicalsAuthorization.acknowledged"] ||
            !topicalsAcknowledgementComplete(data.step3.topicalsAuthorization)) && (
            <p className="field-error mt-2">
              {errors["topicalsAuthorization.acknowledged"] ?? TOPICALS_ACK_REQUIRED_MESSAGE}
            </p>
          )}
        </div>
      </div>

      {/* Round 18 — Over-the-Counter Medication Authorization. Additive: it
          coexists with the medications table + Physician Medication
          Authorization above and is ALWAYS shown (not gated on "takes
          medications?"). Per-item selection is optional; blank = No. */}
      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Over-the-Counter Medication Authorization</h2>
        <p className="text-sm text-slate-700 mb-2">{otcIntroText(cfg.programName)}</p>
        <p className="text-xs text-slate-500 mb-4 italic">{OTC_BLANK_DEFAULT_NOTE}</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                <th className="pr-2 pb-1">Medication</th>
                <th className="pr-2 pb-1">Common use</th>
                <th className="pb-1 text-center">Authorization</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {OTC_ITEMS.map((item) => {
                const current = otcPermissionFor(otcSelectionsSource, item.id);
                const answered = (data.step3.otcAuthorization?.selections ?? []).some(
                  (s) => s.id === item.id
                );
                return (
                  <tr key={item.id} className="align-top">
                    <td className="pr-2 py-2">
                      <div className="font-medium text-slate-800">{item.name}</div>
                    </td>
                    <td className="pr-2 py-2 text-slate-600">{item.use}</td>
                    <td className="py-2">
                      <PermissionRadioGroup
                        name={`otc_${item.id}`}
                        value={answered ? current : null}
                        onChange={(v) => setOtcPermission(item.id, v)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Round 19 — the follow-up question is a required Yes/No selection with
            a required detail box on Yes (same shared component/pattern as the
            Health History grid). Round 20 removed the other three per the
            program's medical supervisor: parallel free-text fields alongside the
            per-item table let a parent contradict their own selections. The
            inline error under each surfaces on a
            partly-filled Step 3 via otcFollowupComplete, since the object-level
            superRefine is skipped by Zod until every required Step 3 field is
            filled (Round 14 gotcha) — mirroring the acknowledgement below. */}
        <div className="mt-6 space-y-4">
          {OTC_QUESTION_FIELDS.map((q) => {
            const followup = data.step3.otcAuthorization?.[q.key];
            const answerError =
              errors[`otcAuthorization.${q.key}.answer`] ??
              (!otcFollowupComplete(followup) && followup?.answer == null
                ? `Please answer: ${q.label}`
                : undefined);
            const detailError =
              errors[`otcAuthorization.${q.key}.detail`] ??
              (followup?.answer === "Yes" && (followup.detail ?? "").trim().length === 0
                ? "Please provide details for your “Yes” answer"
                : undefined);
            return (
              <YesNoWithDetail
                key={q.key}
                name={`otcFollowup_${q.key}`}
                label={q.label}
                value={followup ?? { answer: null, detail: "" }}
                onChange={(next) =>
                  update((d) => {
                    d.step3.otcAuthorization[q.key] = next;
                  })
                }
                errorAnswer={answerError}
                errorDetail={detailError}
              />
            );
          })}
        </div>

        <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-700 mb-3">{otcAcknowledgementText(cfg.programName)}</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-program focus:ring-program"
              checked={!!data.step3.otcAuthorization?.acknowledged}
              onChange={(e) =>
                update((d) => {
                  d.step3.otcAuthorization.acknowledged = e.target.checked;
                })
              }
            />
            <span>
              I have read and acknowledge the Over-the-Counter Medication Authorization above.
            </span>
          </label>
          <div className="mt-4 max-w-md">
            <SignaturePad
              label="Parent / Guardian signature"
              value={data.step3.otcAuthorization?.acknowledgementSignature ?? ""}
              onChange={(v) =>
                update((d) => {
                  d.step3.otcAuthorization.acknowledgementSignature = v || null;
                })
              }
              error={errors["otcAuthorization.acknowledgementSignature"]}
            />
          </div>
          {/* Standalone live check (§6): surfaces on a partly-filled Step 3 even
              though the object-level superRefine is skipped by Zod until every
              other required Step 3 field is filled. */}
          {(errors["otcAuthorization.acknowledged"] ||
            !otcAcknowledgementComplete(data.step3.otcAuthorization)) && (
            <p className="field-error mt-2">
              {errors["otcAuthorization.acknowledged"] ?? OTC_ACK_REQUIRED_MESSAGE}
            </p>
          )}
        </div>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Immunization Status</h2>
        <p className="text-sm text-slate-600 mb-3">
          The following vaccines are recommended for all delegates. Please confirm whether the
          student's immunizations are current.
        </p>
        <ul className="list-disc list-inside text-sm text-slate-700 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          {IMMUNIZATION_VACCINES.map((v) => (
            <li key={v}>{v}</li>
          ))}
        </ul>
        <YesNoRadio
          name="vaccinesUpToDate"
          label="Are the student's vaccines up to date?"
          value={data.step3.vaccinesUpToDate}
          onChange={(v) =>
            update((d) => {
              d.step3.vaccinesUpToDate = v;
              if (v !== "No") d.step3.vaccinesComments = "";
            })
          }
          error={errors["vaccinesUpToDate"]}
        />
        {data.step3.vaccinesUpToDate === "No" && (
          <div className="mt-3">
            <label className="field-label">Please explain which vaccines are not up to date</label>
            <textarea
              className="field-input"
              rows={3}
              value={data.step3.vaccinesComments}
              onChange={(e) =>
                update((d) => {
                  d.step3.vaccinesComments = e.target.value;
                })
              }
            />
            {errors["vaccinesComments"] && <p className="field-error">{errors["vaccinesComments"]}</p>}
          </div>
        )}
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Physician's Statement / Physical Exam</h2>
        <p className="text-sm text-slate-700 mb-3">
          Please upload one of the following: (1) The completed Physician's Statement template below, OR 2) A
          physical exam from a provider within 12 months of the session start date (
          {formatIsoDate(cfg.sessionStartDate)}).
        </p>
        <TemplateUpload
          templateName="physician-statement"
          fieldKey="physician-statement"
          helper="Download the template, have it completed by your physician within the past 12 months, then upload it (or upload a physical exam from a provider covering the same information)."
        />
        <div className="mt-4 max-w-xs">
          <label className="field-label">Date of last physical</label>
          <input
            type="date"
            className="field-input"
            value={data.step3.lastPhysicalDate}
            onChange={(e) =>
              update((d) => {
                d.step3.lastPhysicalDate = e.target.value;
              })
            }
          />
          {errors["lastPhysicalDate"] && <p className="field-error">{errors["lastPhysicalDate"]}</p>}
        </div>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Medical Record (optional)</h2>
        <FileUpload
          fieldKey="medical-record"
          label="Upload additional medical records"
          helper="Optionally upload any additional medical records relevant to the session (e.g., recent specialist notes, lab results). Also use this for lists or documents for maintenance or other medications that will not be taken during the session."
        />
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-2">Physician Medical Information</h2>
        <div className="space-y-4">
          <div>
            <YesNoRadio
              name="accommodationRequest"
              label="I (or my parents) would like to discuss a physical or mental limitation and possible accommodations during the session."
              value={data.step3.accommodationRequest}
              onChange={(v) =>
                update((d) => {
                  d.step3.accommodationRequest = v;
                })
              }
              error={errors["accommodationRequest"]}
            />
            {data.step3.accommodationRequest === "Yes" && (
              <div className="mt-2">
                <label className="field-label">Please describe what you would like to discuss</label>
                <textarea
                  className="field-input"
                  rows={3}
                  value={data.step3.accommodationDescription}
                  onChange={(e) =>
                    update((d) => {
                      d.step3.accommodationDescription = e.target.value;
                    })
                  }
                />
                {errors["accommodationDescription"] && <p className="field-error">{errors["accommodationDescription"]}</p>}
              </div>
            )}
          </div>
          <div>
            <YesNoRadio
              name="healthChangesSincePhysical"
              label="Have any changes in health or medications occurred after the physical exam by the medical professional?"
              value={data.step3.healthChangesSincePhysical}
              onChange={(v) =>
                update((d) => {
                  d.step3.healthChangesSincePhysical = v;
                })
              }
              error={errors["healthChangesSincePhysical"]}
            />
            {data.step3.healthChangesSincePhysical === "Yes" && (
              <div className="mt-2">
                <label className="field-label">Details</label>
                <textarea
                  className="field-input"
                  rows={3}
                  value={data.step3.healthChangesDescription}
                  onChange={(e) =>
                    update((d) => {
                      d.step3.healthChangesDescription = e.target.value;
                    })
                  }
                />
                {errors["healthChangesDescription"] && <p className="field-error">{errors["healthChangesDescription"]}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
